import { Check, Minus } from "lucide-react";
import { FadeUp, LineReveal } from "../components/Reveal";

const CONNECTIONS = [
  "Your work affects your evenings.",
  "Your children affect your schedule.",
  "Your routines affect your mornings.",
  "Your available time affects what kind of side project you can build.",
  "And your own goals compete with everything else.",
];

export const WhyDifferent = () => (
  <section
    id="why-different"
    className="scroll-mt-20 bg-ivory"
    aria-label="Why BloomParentify is different"
    data-testid="why-different-section"
  >
    <div className="mx-auto max-w-[1280px] px-5 py-20 sm:px-8 lg:py-28">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-6">
          <LineReveal
            as="h2"
            lines={["Because your life isn't divided", "into neat categories."]}
            className="font-serif text-4xl font-medium leading-[1.08] tracking-tight text-plum sm:text-5xl"
          />
          <FadeUp delay={0.15} className="mt-6 max-w-xl space-y-4 text-base leading-relaxed text-plumcharcoal/75">
            <p>
              Most products solve one problem. One planner. One meal plan. One
              productivity system. One parenting resource.
            </p>
            <p>But your life doesn't work that way.</p>
          </FadeUp>
          <ul className="mt-6 space-y-3">
            {CONNECTIONS.map((c, i) => (
              <FadeUp key={c} delay={0.2 + i * 0.06}>
                <li className="flex items-start gap-3 text-base text-plum">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                  {c}
                </li>
              </FadeUp>
            ))}
          </ul>
          <FadeUp delay={0.5}>
            <p className="mt-6 max-w-xl text-base font-semibold leading-relaxed text-plum">
              BloomParentify brings those realities together.
            </p>
          </FadeUp>
        </div>

        <div className="lg:col-span-6">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <FadeUp delay={0.1}>
              <div
                data-testid="comparison-other"
                className="flex h-full flex-col rounded-3xl border border-mauve/35 bg-warmwhite p-8"
              >
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-plumcharcoal/50">
                  Typical solutions
                </p>
                <p className="mt-4 font-serif text-2xl italic leading-snug text-plumcharcoal/60">
                  “One problem at a time.”
                </p>
                <ul className="mt-6 space-y-3 text-sm text-plumcharcoal/60">
                  {["A planner for schedules", "An app for tasks", "A book for parenting", "A course for business"].map((t) => (
                    <li key={t} className="flex items-start gap-2.5">
                      <Minus size={15} strokeWidth={1.5} className="mt-0.5 shrink-0 text-plumcharcoal/40" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div
                data-testid="comparison-bloomparentify"
                className="relative flex h-full flex-col rounded-3xl border border-plum bg-plum p-8 shadow-[0_24px_60px_rgba(90,48,73,0.3)]"
              >
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-gold">
                  BloomParentify
                </p>
                <p className="mt-4 font-serif text-2xl italic leading-snug text-ivory">
                  “Your whole life in context.”
                </p>
                <ul className="mt-6 space-y-3 text-sm text-ivory/85">
                  {["Life organization and ambition together", "Kids' activities next to your calendar", "Personal time built into the plan", "Income exploration that fits real schedules"].map((t) => (
                    <li key={t} className="flex items-start gap-2.5">
                      <Check size={15} strokeWidth={2} className="mt-0.5 shrink-0 text-gold" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
          </div>
        </div>
      </div>
    </div>
  </section>
);
